import UserAvatar from "@/components/ui/UserAvatar";
import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import {
  Search, Plus, Send, Ban, CheckCircle2, Trash2, Pencil, Crown, Users2, XCircle, Eye,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SearchClearButton from "@/components/ui/SearchClearButton";
import { AvatarBlock } from "@/components/settings/AccountSection";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import DeskConfirm from "@/components/crm/DeskConfirm";
import UserFormDialog from "@/components/settings/UserFormDialog";
import UserRightsDialog from "@/components/settings/rights/UserRightsDialog";
import ProfilesManager from "@/components/settings/rights/ProfilesManager";
import UserReasonDialog from "@/components/settings/UserReasonDialog";
import api from "@/lib/api";
import { beginImpersonation, readImpersonationOrigin } from "@/components/ImpersonationBanner";
import { skipNextUnloadPrompt } from "@/lib/leaveRegistry";
import { clearUnsavedChanges } from "@/lib/unsavedChanges";
import { Crown as CrownIcon, ShieldCheck, TableProperties, FileType } from "lucide-react";

const ROLE_LABEL = { admin: "Administrateur", manager: "Manager", user: "Utilisateur" };
const STATUS_LABEL = { active: "Actif", invited: "En attente", blocked: "Bloqué", inactive: "Désactivé" };
const STATUS_COLOR = {
  active:   "bg-transparent text-status-success border-status-success/50",
  invited:  "bg-transparent text-status-pending border-status-pending/50",
  blocked:  "bg-transparent text-status-danger border-status-danger/50",
  inactive: "bg-transparent text-muted-foreground border-vertec-borderDark",
};
const ROLE_ACCENT = {
  admin:   "border-status-danger/40 text-status-danger",
  manager: "border-vertec-blue/40 text-vertec-blue",
  user:    "border-muted text-muted-foreground",
};

export default function UsersSection({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: "all", status: "all", project: "all" });
  const [search, setSearch] = useState("");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(false); // iter188 — ouvre « Gestion des profils »
  const [rightsUser, setRightsUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  useEffect(() => { api.get("/rights/profiles").then(({ data }) => setProfiles(data)).catch(() => {}); }, []);

  const isFirstAdmin = !!currentUser?.is_first_admin;
  const [zohoStates, setZohoStates] = useState({});
  useEffect(() => { if (isFirstAdmin) api.get("/zoho/status/all").then(({ data }) => setZohoStates(data.states || {})).catch(() => {}); }, [isFirstAdmin]);
  const canEditMatrix = isFirstAdmin;

  // KPI band (spec 2026-07-18 undecies).
  const kpi = useMemo(() => {
    const c = { total: users.length, first_admin: 0, admin: 0, manager: 0, user: 0 };
    users.forEach((u) => {
      if (u.is_first_admin) c.first_admin += 1;
      else if (u.role === "admin") c.admin += 1;
      else if (u.role === "manager") c.manager += 1;
      else c.user += 1;
    });
    return c;
  }, [users]);


  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch {
      toast.error("Impossible de charger les utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  // Spec iter193 (2026-02-28) — colonne « Urgent (mois) » retirée :
  // n'apportait aucune information exploitable en pratique. Le
  // composant `UrgentCountCell` est conservé au cas où la métrique
  // serait ré-introduite ailleurs (ex. dashboard analytique).
  // Spec iter110 Partie 2 — deep-link `?highlight=<uid>` depuis le badge
  // Responsable des étapes de Documents. Scrolle + surligne la ligne 3 s.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightUid = searchParams.get("highlight") || "";
  const rightsUid = searchParams.get("rights") || "";
  useEffect(() => {
    if (!rightsUid || loading) return;
    const u = users.find((x) => x.id === rightsUid);
    if (u) { setRightsUser(u); setSearchParams((sp) => { sp.delete("rights"); return sp; }, { replace: true }); }
  }, [rightsUid, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [highlightActive, setHighlightActive] = useState("");
  const highlightTimerRef = useRef(null);

  useEffect(() => { load(); }, []);

  // Spec iter110 Partie 2 — après chargement, si `highlight` correspond
  // à un utilisateur visible → scroll + surlignage temporaire (3 s) puis
  // nettoyage du query param.
  useEffect(() => {
    if (!highlightUid || loading) return;
    if (!users.some(u => u.id === highlightUid)) return;
    setHighlightActive(highlightUid);
    // Petit délai pour laisser la table rendre le DOM.
    const t1 = setTimeout(() => {
      const row = document.querySelector(`[data-testid="user-row-${highlightUid}"]`);
      if (row && row.scrollIntoView) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
    highlightTimerRef.current = setTimeout(() => {
      setHighlightActive("");
      // Nettoie l'URL sans ajouter une entrée d'historique.
      const next = new URLSearchParams(searchParams);
      next.delete("highlight");
      setSearchParams(next, { replace: true });
    }, 3000);
    return () => {
      clearTimeout(t1);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightUid, loading, users.length]);

  const projects = useMemo(() => {
    const set = new Set(users.map((u) => u.default_project).filter(Boolean));
    return Array.from(set).sort();
  }, [users]);

  const managerMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = `${u.first_name} ${u.last_name}`; });
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filters.role !== "all" && u.role !== filters.role) return false;
      if (filters.status !== "all" && u.status !== filters.status) return false;
      if (filters.project !== "all" && u.default_project !== filters.project) return false;
      if (!q) return true;
      const hay = `${u.first_name} ${u.last_name} ${u.email} ${u.function || ""} ${u.default_project || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, filters, search]);

  return (
    <Card className="bg-card border-vertec-borderDark p-8">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2">
            <Users2 size={22} className="text-vertec-blue" /> Utilisateurs
          </h2>
          <p className="text-muted-foreground text-sm">
            Invitez et gérez les membres de l&apos;équipe. Le Premier Admin est protégé et ne peut être
            ni bloqué ni supprimé.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canEditMatrix && (
            <Button
              variant="outline"
              className="gap-2 border-vertec-borderDark"
              onClick={() => setMatrixOpen(true)}
              data-testid="btn-open-profiles"
              title="Gestion des profils de droits"
            >
              <TableProperties size={15} /> Gestion des profils
            </Button>
          )}
          <Button
            className="bg-vertec-blue hover:bg-vertec-blueHover text-white gap-2 shrink-0"
            onClick={() => setInviteOpen(true)}
            data-testid="btn-invite-user"
          >
            <Plus size={16} /> Inviter
          </Button>
        </div>
      </div>

      {/* --- KPI band (6 cartes égales — spec 2026-07-18 undecies) ------ */}
      <div className="grid mb-6" style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 6 }}>
        {[
          { key: "total",       label: "Total",         value: kpi.total,       color: "#2B579A", Icon: Users2,      filled: true },
          { key: "first_admin", label: "Premier Admin", value: kpi.first_admin, color: "#F5C842", Icon: CrownIcon,   filled: false },
          { key: "admin",       label: "Admin",         value: kpi.admin,       color: "#EF4444", Icon: ShieldCheck, filled: false },
          { key: "manager",     label: "Manager",       value: kpi.manager,     color: "#0078D4", Icon: Users2,      filled: false },
          { key: "user",        label: "Utilisateur",   value: kpi.user,        color: "#9CA3AF", Icon: Users2,      filled: false },
        ].map(({ key, label, value, color, Icon, filled }) => (
          <div
            key={key}
            className="flex flex-col items-center justify-center rounded-md"
            style={{
              background: "#17171B",
              border: filled ? `1px solid ${color}` : "1px solid #26262C",
              color: "#FFFFFF",
              padding: "6px 4px", minHeight: 60,
            }}
            data-testid={`users-kpi-${key}`}
          >
            <Icon size={13} strokeWidth={2.2} style={{ color }} />
            <span className="font-bold tabular-nums"
                  style={{ fontSize: 20, lineHeight: 1, marginTop: 4 }}>{value}</span>
            <span className="uppercase mt-0.5 whitespace-nowrap"
                  style={{ fontSize: 10, letterSpacing: "0.03em",
                           color: "#9a9a95", fontWeight: 700 }}>
              {label}
            </span>
          </div>
        ))}
        {/* + Inviter card */}
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="flex flex-col items-center justify-center rounded-md transition-all hover:brightness-110"
          style={{
            background: "#17171B", color: "#3B82F6",
            padding: "6px 4px", minHeight: 60, cursor: "pointer",
            border: "1px solid #26262C", fontWeight: 700,
          }}
          data-testid="users-kpi-invite"
        >
          <span aria-hidden="true" className="tabular-nums"
            style={{ fontSize: 26, lineHeight: 1, color: "#3B82F6", fontWeight: 700 }}>+</span>
          <span className="uppercase mt-1 whitespace-nowrap"
            style={{ fontSize: 10, letterSpacing: "0.03em", fontWeight: 700, color: "#9a9a95" }}>
            Inviter
          </span>
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5">
        <div className="relative md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            placeholder="Rechercher…"
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="user-search"
            data-no-capitalize
          />
          <SearchClearButton value={search} onClear={() => setSearch("")} testid="user-search-clear" />
        </div>
        <Select value={filters.role} onValueChange={(v) => setFilters({ ...filters, role: v })}>
          <SelectTrigger data-testid="filter-role"><SelectValue placeholder="Rôle" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            <SelectItem value="admin">Administrateur</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="user">Utilisateur</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="active">Actif</SelectItem>
            <SelectItem value="invited">En attente</SelectItem>
            <SelectItem value="blocked">Bloqué</SelectItem>
            <SelectItem value="inactive">Désactivé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.project} onValueChange={(v) => setFilters({ ...filters, project: v })}>
          <SelectTrigger data-testid="filter-project"><SelectValue placeholder="Siège / Projet" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les sièges / projets</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm py-10 text-center">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground text-sm py-10 text-center border border-dashed border-vertec-borderDark rounded-md">
          Aucun utilisateur ne correspond aux filtres.
        </div>
      ) : (
        <div className="rounded-md border border-vertec-borderDark overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-vertec-borderDark bg-background/40">
                <TableHead className="w-[280px]">Membre</TableHead>
                <TableHead>Fonction</TableHead>
                <TableHead>Département</TableHead>
                <TableHead>Siège / Projet</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Statut</TableHead>
                {isFirstAdmin && <TableHead>Zoho</TableHead>}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  currentUser={currentUser}
                  managerName={u.manager_id ? managerMap[u.manager_id] : null}
                  onEdit={() => setEditUser(u)}
                  onChanged={load}
                  isFirstAdmin={isFirstAdmin}
                  setRightsUser={setRightsUser} profiles={profiles}
                  highlighted={highlightActive === u.id}
                  zohoState={isFirstAdmin ? (u.zoho_email ? (zohoStates[u.id] || "none") : null) : undefined}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <UserFormDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        mode="invite"
        currentUser={currentUser}
        onSaved={load}
      />
      <UserFormDialog
        open={!!editUser}
        onOpenChange={(open) => !open && setEditUser(null)}
        mode="edit"
        currentUser={currentUser}
        user={editUser}
        onSaved={load}
      />

      {matrixOpen && canEditMatrix && <ProfilesManager onClose={() => setMatrixOpen(false)} onChanged={() => { load(); api.get("/rights/profiles").then(({ data }) => setProfiles(data)).catch(() => {}); }} />}
      {rightsUser && <UserRightsDialog user={rightsUser} onClose={() => setRightsUser(null)} onSaved={() => { load(); api.get("/rights/profiles").then(({ data }) => setProfiles(data)).catch(() => {}); }} />}
    </Card>
  );
}

const ZOHO_STATE = { connected: ["Connecté", "#37c99a"], reconnect: ["Reconnexion requise", "#E0A42E"], none: ["Non connecté", "#6b6b74"] };

function UserRow({ u, currentUser, managerName, onEdit, onChanged, isFirstAdmin: viewerIsFirstAdmin, setRightsUser, profiles = [], highlighted = false, zohoState }) {
  const prof = profiles.find((p) => p.id === (u.is_first_admin ? "super_admin" : u.profile_id));
  const [busy, setBusy] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelInviteOpen, setCancelInviteOpen] = useState(false);

  const isSelf = u.id === currentUser.id;
  const isFirstAdmin = u.is_first_admin;
  const currentIsFirstAdmin = currentUser.is_first_admin;

  const canEdit = currentIsFirstAdmin || (!isFirstAdmin && u.role !== "admin");
  const canManagePending = !isFirstAdmin && !isSelf && (currentIsFirstAdmin || u.role !== "admin");
  const canBlock = !isFirstAdmin && !isSelf && u.status !== "invited" && u.status !== "inactive" &&
    (currentIsFirstAdmin || u.role !== "admin");
  const canDelete = !isFirstAdmin && !isSelf && u.status !== "invited" && u.status !== "inactive" &&
    (currentIsFirstAdmin || u.role !== "admin");

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error("Action refusée", { description: err.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success("Compte réactivé");
      onChanged();
    } catch (err) {
      toast.error("Action refusée", { description: err.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const suspendWithReason = async (reason) => {
    await api.post(`/users/${u.id}/suspend`, { reason });
    toast.success("Compte suspendu");
    onChanged();
  };

  const deleteWithReason = async (reason) => {
    await api.delete(`/users/${u.id}`, { data: { reason } });
    toast.success("Utilisateur supprimé");
    onChanged();
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${u.id}/resend-invite`);
      toast.success("Invitation renvoyée");
      onChanged();
    } catch (err) {
      toast.error("Action refusée", { description: err.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const cancelInvite = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`, { data: { reason: "Invitation annulée" } });
      toast.success("Invitation annulée");
      onChanged();
    } catch (err) {
      toast.error("Action refusée", { description: err.response?.data?.detail });
    } finally { setBusy(false); }
  };

  const initials = `${(u.first_name?.[0] || "").toUpperCase()}${(u.last_name?.[0] || "").toUpperCase()}`;

  return (
    <TableRow
      className={`border-vertec-borderDark transition-all ${highlighted ? "ring-1 ring-[#5b5fc7]" : ""}`}
      data-testid={`user-row-${u.id}`}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <UserAvatar user={u} size={36} showPopover={false} testid={`user-avatar-${u.id}`} />
          <div className="min-w-0">
            <div className="font-medium truncate flex items-center gap-1.5">
              {u.first_name} {u.last_name}
              {isFirstAdmin && (
                <span title="Premier Admin (protégé)" className="text-status-pending">
                  <Crown size={12} />
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-sm">{u.function || <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-sm" data-testid={`user-department-${u.id}`}>{u.department || <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-sm">{u.default_project || <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell className="text-sm">{managerName || <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell>
        <div className="inline-flex items-center gap-1.5 flex-wrap">
          {prof ? (
            <button type="button" onClick={() => setRightsUser?.(u)} className="inline-flex items-center gap-1.5" title={prof.description} data-testid={`user-profile-${u.id}`}
              style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 6, background: "transparent", color: prof.color || "#c9c2ff", border: `1px solid ${prof.color || "#7F77DD"}99` }}>
              {u.is_first_admin && <CrownIcon size={10} />}{prof.name}{u.rights_custom && !u.is_first_admin && <span style={{ fontSize: 9, color: "#E0822E" }}>· Sur mesure</span>}
            </button>
          ) : <Badge variant="outline" className={ROLE_ACCENT[u.role]}>{ROLE_LABEL[u.role]}</Badge>}
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={u.archived_at ? "bg-transparent text-status-danger border-status-danger/50" : STATUS_COLOR[u.status]}
          title={u.archived_at
            ? (u.archive_reason ? `Motif : ${u.archive_reason}` : "Compte supprimé")
            : (u.status === "blocked" && u.blocked_reason ? `Motif : ${u.blocked_reason}` : undefined)}
        >
          {u.archived_at ? "Supprimé" : STATUS_LABEL[u.status]}
        </Badge>
      </TableCell>
      {zohoState !== undefined && <TableCell data-testid={`user-zoho-${u.id}`} data-state={zohoState || ""}>
        {zohoState ? <span className="inline-flex items-center" style={{ gap: 6, fontSize: 11, color: ZOHO_STATE[zohoState][1] }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: ZOHO_STATE[zohoState][1] }} />{ZOHO_STATE[zohoState][0]}</span> : <span className="text-muted-foreground">—</span>}
      </TableCell>}
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {/* Spec iter193 (2026-02-28) — « Se connecter en tant que »
              réservé au Premier Admin, invisible sur sa propre ligne.
              Œil dans pastille teintée jaune ; ouvre une session sous
              l'identité cible (bandeau de retour côté layout). */}
          {viewerIsFirstAdmin && !isSelf && !readImpersonationOrigin() && (
            <ImpersonateButton u={u} adminName={`${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() || currentUser.email} />
          )}
          {u.status === "invited" && (
            <Button
              variant="ghost" size="sm" className="gap-1.5 h-8"
              onClick={resend} disabled={busy}
              data-testid={`btn-resend-${u.id}`}
              title={u.invite_resend_count > 0
                ? `Invitation renvoyée ${u.invite_resend_count} fois`
                : "Renvoyer l'invitation"}
            >
              <Send size={14} /> Renvoyer
              {u.invite_resend_count > 0 && (
                <span
                  className="ml-0.5 text-[10px] font-semibold tabular-nums text-vertec-blue"
                  data-testid={`resend-count-${u.id}`}
                >
                  (×{u.invite_resend_count})
                </span>
              )}
            </Button>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setRightsUser(u)} title="Droits d'accès" data-testid={`btn-access-rights-${u.id}`}>
              <ShieldCheck size={14} className="text-[#a68ee0]" />
            </Button>
          )}
          {isFirstAdmin && (<>{null}</>)}
          {canEdit && (
            <Button
              variant="ghost" size="sm" className="h-8"
              onClick={onEdit} disabled={busy}
              data-testid={`btn-edit-${u.id}`}
            >
              <Pencil size={14} />
            </Button>
          )}
          {canBlock && (
            <Button
              variant="ghost" size="sm" className="gap-1.5 h-8"
              onClick={() => (u.status === "blocked" ? reactivate() : setSuspendOpen(true))}
              disabled={busy}
              data-testid={`btn-block-${u.id}`}
              title={u.status === "blocked"
                ? (u.blocked_reason ? `Motif de suspension : ${u.blocked_reason}` : "Réactiver le compte")
                : "Suspendre le compte (motif obligatoire)"}
            >
              {u.status === "blocked"
                ? <><CheckCircle2 size={14} className="text-status-success" /></>
                : <><Ban size={14} className="text-status-danger" /></>}
            </Button>
          )}
          {u.status === "invited" && canManagePending && (
            <>
              <Button variant="ghost" size="sm" className="gap-1.5 h-8 text-status-danger"
                disabled={busy} onClick={() => setCancelInviteOpen(true)}
                data-testid={`btn-cancel-invite-${u.id}`}>
                <XCircle size={14} /> Annuler l&apos;invitation
              </Button>
              <DeskConfirm open={cancelInviteOpen} icon={XCircle} title="Annuler cette invitation ?"
                message={`L'invitation envoyée à ${u.email} sera révoquée et le lien d'activation deviendra invalide. Vous pourrez réinviter cette personne à tout moment.`}
                cancelLabel="Retour" confirmLabel="Annuler l'invitation" onClose={() => setCancelInviteOpen(false)} onConfirm={() => { setCancelInviteOpen(false); cancelInvite(); }} testId={`btn-confirm-cancel-invite-${u.id}`} />
            </>
          )}
          {canDelete && (
            <Button
              variant="ghost" size="sm" className="h-8 text-status-danger"
              onClick={() => setDeleteOpen(true)}
              disabled={busy}
              data-testid={`btn-delete-${u.id}`}
              title="Supprimer l'utilisateur (motif obligatoire)"
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>

        {/* Dialogues motif obligatoire — spec 2026-08-05 */}
        <UserReasonDialog
          open={suspendOpen}
          onOpenChange={setSuspendOpen}
          title={`Suspendre ${u.first_name} ${u.last_name} ?`}
          description={
            <span>
              Le compte <strong>{u.email}</strong> sera immédiatement bloqué et l&apos;utilisateur
              ne pourra plus se connecter. Vous pourrez le réactiver à tout moment.
            </span>
          }
          submitLabel="Suspendre"
          submitVariant="danger"
          onSubmit={suspendWithReason}
          testidPrefix={`suspend-${u.id}`}
        />
        <UserReasonDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title={`Supprimer ${u.first_name} ${u.last_name} ?`}
          description={
            <span>
              Le compte <strong>{u.email}</strong> sera archivé et l&apos;utilisateur perdra tout
              accès à l&apos;application. Ses données passées (tâches, documents, historique)
              restent conservées pour l&apos;audit.
            </span>
          }
          submitLabel="Supprimer"
          submitVariant="danger"
          onSubmit={deleteWithReason}
          testidPrefix={`delete-${u.id}`}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * Bouton œil « Se connecter en tant que » — appelle
 * `/api/auth/impersonate`, mémorise le token admin d'origine et
 * recharge l'app sous l'identité cible.
 */
function ImpersonateButton({ u, adminName }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/impersonate", { user_id: u.id });
      const adminToken = sessionStorage.getItem("ledesk_token");
      if (!adminToken) throw new Error("Token admin introuvable");
      beginImpersonation({
        adminToken,
        adminName,
        targetName: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
        sessionId: data.session_id,
      });
      sessionStorage.setItem("ledesk_token", data.token);
      toast.success(`Session ouverte en tant que ${u.first_name} ${u.last_name}`);
      // iter425 pt 1 — ouverture immédiate, sans aucune fenêtre (ni beforeunload, ni « Quitter l'application ? »).
      clearUnsavedChanges();
      skipNextUnloadPrompt();
      setTimeout(() => { window.location.href = "/"; }, 400);
    } catch (err) {
      toast.error("Impossible d'ouvrir la session", {
        description: err.response?.data?.detail || err.message,
      });
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0 rounded-full"
      onClick={handleClick}
      disabled={busy}
      title={`Se connecter en tant que ${u.first_name} ${u.last_name}`}
      data-testid={`btn-impersonate-${u.id}`}
      style={{
        background: "transparent",
        border: "1px solid #33333B",
      }}
    >
      <Eye size={14} style={{ color: "#F5C842" }} />
    </Button>
  );
}
